import { ABEventDateFilter } from '../ab/widgets-home';
import { CBookingPartId } from '../mdl/c-booking';
import { CEventId } from '../mdl/c-event';
import { CEventTagId } from '../mdl/c-event-tag';
import { CProducerId } from '../mdl/c-producer';
import { CPaidPlanId } from '../mdl/c-user';
import { CVenueId } from '../mdl/c-venue';
import { MChannel } from '../mdl/m-message';
import { meCommitPreferences } from '../me/preferences';
import { UIMenuHeaderOptions } from '../ui/component/menu-bar';
import { UIMenu } from '../ui/menu';
import { UIScreen } from '../ui/screen';
import { UIValue } from '../ui/types';
import { $log, xavis_experiments } from './debug';
import { $history } from './history';
import { YLanguage } from './i18n';
import { yCopy, yEqual } from './object';
import { y_base_path, yPathToUrl, ySetBasePath } from './path';
import { YFlags, YId } from './types';
import { yMaybeDeferredWithRejectValue } from './util';

export interface YLocationParams {
    event_id?: CEventId;
    tag_id?: CEventTagId;
    venue_id?: CVenueId;
    city_slug?: string;
    event_slug?: string;
    producer_slug?: string;
    venue_slug?: string;
    genre_slug?: string;
    tag_slug?: string;
    booking_part_id?: CBookingPartId;
    imported_id?: YId;
    session_id?: YId;
    template_id?: YId;
    producer_id?: CProducerId;
    page_id?: YId;
    route_id?: YId;
    user_id?: YId;
    languages?: YFlags<YLanguage>;
    channel?: MChannel;
    log_id?: YId;
    duplicate?: boolean;
    options?: any;
    master_url?: string;
    position?: number;
    gc_code?: string;
    plan?: CPaidPlanId;
    search_text?: string;
    date_filter?: ABEventDateFilter;
    from?: number;
    to?: number;

    event_list_id?: string;
    resubscribe?: boolean;
    onboarding?: boolean;
    origin?: string;
    flow?: string;
    video?: boolean;
    tab?: string;
};

export interface YLocation {
    name?: undefined;
    path: string[];
    params: YLocationParams;
    transition_params?: YLocationParams;
}

export type YLocationArg = string[] | YLocation;

export interface YPushSpec {
    name: string;
    path?: undefined;
    params: YLocationParams;
    transition_params?: YLocationParams;
}

export interface YPushTransient {
    transient: UIScreen;
}

export type YPushArg = string | YPushSpec | YPushTransient;

export interface YStackItem {
    id?: number;
    spec?: YPushSpec | YLocation;
}

export type YScrollState = { [id: string]: [number, number] };

export interface YLocationState {
    stack: YStackItem[];
    scrolls: Record<number, YScrollState>
}

export interface YRoute {
    name?: string;
    path?: string[];
    paths?: string[][];
    hosts?: string[];
    match?: (location: YLocation | YPushSpec) => boolean | YLocationParams;

    redirect?: YLocationArg | ((match: YLocationParams) => YLocationArg | undefined);
    screen?: (match: YLocationParams) => UIScreen | undefined;
    menu?: (match: YLocationParams) => UIMenu | undefined;
    menu_header_options?: (match: YLocationParams) => UIMenuHeaderOptions | undefined;
}

export type YNavigationResult = boolean | Promise<boolean>;

interface YLocationComposer {
    show(screen: UIScreen, scroll?: YScrollState, menu?: UIMenu, menu_options?: UIMenuHeaderOptions, transition_params?: YLocationParams, back?: boolean, reload?: boolean, reappear?: boolean): void;
    canNavigate(): YNavigationResult;
}

export namespace $location {
    ///////////////////
    ///////////////////
    ///// Utility /////
    ///////////////////
    ///////////////////
    export function getURL(url: string) {
        return new URL(url, window.location.href);
    }

    export function getCurrentQuery() {
        return Object.fromEntries(new URLSearchParams(window.location.search));
    }

    export function urlToLocation(url: string, merge?: YLocationParams): YLocation {
        const resolved = getURL(url);
        const pathname = resolved.pathname;
        const path = pathname.startsWith(y_base_path) ? pathname.substring(y_base_path.length).split('/').filter(s => s).map(decodeURIComponent) : [];
        return { path: path, params: Object.assign(Object.fromEntries(resolved.searchParams), merge) };
    }

    ////////////////////
    ////////////////////
    ////// Router //////
    ////////////////////
    ////////////////////
    const host = window.location.host;
    let routes: YRoute[];

    function pathMatches(path: string[], pattern: string[]): YLocationParams | undefined {
        if(path.length > pattern.length) return;

        const match: YLocationParams = {};

        for(let i = 0; i < pattern.length; i++) {
            const p = pattern[i];
            const v = path[i];
            if(p[0] == '%') {
                if(p[1] == '?') {
                    if(v != null && p.length > 2)
                        match[p.substring(2) as keyof YLocationParams] = v;
                } else {
                    if(v == null) return;
                    if(p.length > 1)
                        match[p.substring(1) as keyof YLocationParams] = v;
                }
            } else if(p !== v) {
                return;
            }
        }

        return match;
    }

    function matchRoute(route: YRoute, spec: YLocation | YPushSpec): YLocationParams | undefined {
        let match: YLocationParams = {};

        if(spec.name != undefined) {
            if(!route.name || route.name != spec.name) return;
        } else {
            if(route.name) return;

            if(route.path) {
                let params = pathMatches(spec.path, route.path);
                if(!params) return;
                Object.assign(match, params);
            } else if(xavis_experiments && route.paths) {
                let params: YLocationParams | undefined
                for(const p of route.paths) {
                    if(params = pathMatches(spec.path, p))
                        break;
                }
                if(!params) return;
                Object.assign(match, params);
            }

            if(route.hosts) {
                if(!route.hosts.includes(host)) return;
            }
        }

        if(route.match) {
            const params = route.match(spec);
            if(!params) return;
            if(params !== true)
                Object.assign(match, params);
        }

        return Object.assign({}, spec.params, match);
    }

    function getRoute(spec: YLocation | YPushSpec): [YRoute, YLocationParams] | undefined {
        for(const r of routes) {
            const match = matchRoute(r, spec);
            if(match)
                return [r, match];
        }

        return;
    }

    function resolveRoute(spec: YLocation | YPushSpec, loop_detect: number = 0): [YLocation | YPushSpec, YRoute, YLocationParams] | undefined {
        if(loop_detect > 10) {
            throw 'Navigation loop!';
        }

        const result = getRoute(spec);
        if(!result) return undefined;

        const route = result[0];
        const match = result[1];

        if(route.redirect) {
            let redirect = (typeof route.redirect === 'function') ? route.redirect(match) : route.redirect;

            if(redirect) {
                if(Array.isArray(redirect))
                    redirect = { path: redirect, params: {} }
                return resolveRoute(redirect, loop_detect + 1);
            }
        }

        return [spec, route, match];
    }

    /////////////////////////
    /////////////////////////
    // Internal navigation //
    /////////////////////////
    /////////////////////////

    function getInitialLocation(): YLocationState {
        const url = window.location.pathname;
        const path = url.startsWith(y_base_path) ? url.substring(y_base_path.length).split('/').filter(s => s).map(decodeURIComponent) : [];
        return { stack: [{ spec: { path: path, params: $location.getCurrentQuery() } }], scrolls: {} };
    }

    function stateToUrl(state: YLocationState, append_query?: boolean) {
        const path = yPathToUrl(state.stack[0].spec!.path!);
        return append_query ? path + window.location.search : path;
    }

    function isValidState(state: YLocationState) {
        return typeof state === 'object' && Array.isArray(state.stack) && state.stack.length > 0 && Array.isArray(state.stack[0].spec?.path) && typeof state.scrolls == 'object';
    }

    interface NavigationEntry {
        screen: UIScreen;
        menu?: UIMenu;
        menu_header_options?: UIMenuHeaderOptions;
        transition_params?: YLocationParams;
    }

    let composer: YLocationComposer;
    let next_id = new Date().getTime();
    let current_stack: YStackItem[] = [];
    let entries: Record<number, NavigationEntry> = {};
    let scrolls: Record<number, YScrollState> = {};
    export let manual_scroll: boolean;

    function makeNavigationEntry(route: YRoute, match: YLocationParams, transition_params?: YLocationParams | undefined): NavigationEntry {
        return { screen: route.screen!(match)!, menu: route.menu?.(match), menu_header_options: route.menu_header_options?.(match), transition_params }
    }

    export let on_navigation: (() => void) | undefined;
    export let title_suffix = '';

    type NavigationMode = 'normal' | 'reload' | 'initial' | 'back' | 'forward';

    function equalStacks(a: YStackItem[], b: YStackItem[]) {
        if(a.length != b.length)
            return false;
        for(let i = 0; i < a.length; i++) {
            if(a[i].id != b[i].id)
                return false;
        }

        return true;
    }

    function doStack(stack: YStackItem[], commit: (new_state: YLocationState) => void, cancel: (() => void) | undefined, mode: NavigationMode) {
        $log('navigation', 'doNavigate', yCopy(stack), mode);

        if(equalStacks(stack, current_stack) && mode != 'reload') {
            $log('navigation', 'states are equal and it\'s not reload... why would this happen?');
            return;
        }

        yMaybeDeferredWithRejectValue(mode === 'initial' || composer.canNavigate(), can_navigate => {
            if(!can_navigate) {
                cancel?.();
                return;
            }

            //need to build the stack from the current_state and the new state;
            const new_stack: YStackItem[] = [];
            const new_entries: { [id: number]: NavigationEntry } = {};
            let is_new = false;

            for(let i = 0; i < stack.length; i++) {
                const new_item = stack[i];

                if(new_item.id && entries[new_item.id]) {
                    const entry = entries[new_item.id];
                    new_entries[new_item.id] = entry;
                    new_stack.push(stack[i]);
                    is_new = false;
                } else if(new_item.spec) {
                    const resolved = resolveRoute(new_item.spec);
                    if(!resolved)
                        break;
                    const entry = makeNavigationEntry(resolved[1], resolved[2], new_item.spec.transition_params);
                    const id = new_item.id ?? next_id++;
                    new_entries[id] = entry;
                    new_stack.push({ id, spec: resolved[0] });
                    is_new = true;
                } else {
                    break;
                }
            }

            if(!new_stack.length) {
                cancel?.();
                return;
            }

            // screen.on_update_menu && screen.on_update_menu(location, this._menu_header);
            // TODO: maybe go through the stack and notify the views?
            // content.saved_state = undefined;

            if(manual_scroll) {
                //save scroll
                if(current_stack.length) {
                    const scroll_state: YScrollState = { root: [window.scrollX, window.scrollY] };
                    for(const v of document.body.getElementsByClassName('x-scroll-id')) {
                        if(v.ui_scroll_id)
                            scroll_state[v.ui_scroll_id] = [v.scrollLeft, v.scrollTop];
                    }

                    scrolls[current_stack[current_stack.length - 1].id!] = scroll_state;
                    $log('app', 'saving scroll state', scroll_state);
                }
            }

            entries = new_entries;
            current_stack = new_stack;
            commit({ stack: current_stack, scrolls });
            // if(xavis_experiments)
            //     $composer.newShow(entry);
            // else
            const top_id = current_stack[current_stack.length - 1].id!;
            const entry = entries[top_id]
            composer.show(entry.screen, scrolls[top_id], entry.menu, entry.menu_header_options, entry.transition_params, mode == 'back', mode == 'reload', !is_new);

            // if(content.saved_scroll) {
            //     window.scrollTo(0,content.saved_scroll);
            //     content.saved_scroll = undefined;
            // } else {
            //     window.scrollTo(0,0);
            // }

            on_navigation && on_navigation();
            meCommitPreferences();
        }, false);
    }

    export function init(_routes: YRoute[], _base_path: string, _composer: YLocationComposer) {
        composer = _composer;
        manual_scroll = xavis_experiments && !!document.startViewTransition
        if(manual_scroll)
            history.scrollRestoration = 'manual';

        if(!_base_path.endsWith('/'))
            _base_path += '/';
        routes = _routes;
        ySetBasePath(_base_path);

        $history.init((state, commit, cancel, mode) => {
            if(state.scrolls) {
                for(const id in state.scrolls) {
                    if(scrolls[id] === undefined)
                        scrolls[id] = state.scrolls[id];
                }
            }
            doStack(state.stack, commit, cancel, mode);
        }, getInitialLocation, stateToUrl, isValidState);
    }

    //////////////////////////
    //////////////////////////
    /////// Navigation ///////
    //////////////////////////
    //////////////////////////

    export function at(path: string[]) {
        const last = current_stack[current_stack.length - 1];
        return yEqual(last?.spec?.path, path)
    }

    export function under(path: string[]) {
        const last = current_stack[current_stack.length - 1];
        const last_path = last?.spec?.path;
        if(!last_path) return false;
        if(last_path.length < path.length) return false;
        return yEqual(last_path.slice(0, path.length), path)
    }

    export function reload() {
        const last = current_stack[current_stack.length - 1];
        if(last.spec && last.id) {
            delete entries[last.id];
            doStack(current_stack, $history.replace, undefined, 'reload');
        }
    }

    export function navigate(path: string[], params?: YLocationParams): void
    export function navigate(location: YLocation): void
    export function navigate(location: YLocationArg, params?: YLocationParams) {
        if(Array.isArray(location))
            location = { path: location, params: params || {} }

        $log('navigation', 'navigating to ', location);
        doStack([{ spec: location }], $history.push, undefined, 'normal');
    }

    function makePushEntry(spec: YPushArg, params?: YLocationParams): YStackItem {
        if(typeof spec === 'string') {
            return { spec: { name: spec, params: params || {} } };
        } else if('transient' in spec) {
            const id = next_id++;
            entries[id] = { screen: spec.transient, menu_header_options: spec.transient.no_crumbs ? { no_crumbs: true } : undefined };
            return { id };
        } else {
            return { spec };
        }
    }

    export function push(name: string, params?: YLocationParams): void
    export function push(spec: YPushSpec): void
    export function push(spec: YPushTransient): void
    export function push(spec: YPushArg, params?: YLocationParams) {
        doStack([...current_stack, makePushEntry(spec, params)], $history.push, undefined, 'normal');
    }

    export function popPush(name: string, params?: YLocationParams): void
    export function popPush(spec: YPushSpec): void
    export function popPush(spec: YPushTransient): void
    export function popPush(spec: YPushArg, params?: YLocationParams) {
        doStack([...current_stack.slice(0, -1), makePushEntry(spec, params)], $history.replace, undefined, 'normal');
    }

    export function pop() {
        if(current_stack.length > 1)
            history.go(-1);
    }

    export function popTo(i: number) {
        if(current_stack.length > i + 1)
            history.go(i - current_stack.length + 1);
    }

    export function getCrumbs() {
        if(current_stack.length < 2) return undefined;
        let crumbs: UIValue<number>[] = [];

        for(let i = 0; i < current_stack.length; i++) {
            const entry = entries[current_stack[i].id!];
            if(!entry) continue;
            crumbs.push({ key: i, name: entry.screen.title || '' });
        }

        return crumbs;
    }
}

$log('app', 'initializing location');